import { Extension, type ChainedCommands, type Editor, type Range } from '@tiptap/core';
import { Suggestion, type SuggestionProps } from '@tiptap/suggestion';

export interface SlashCommand {
  title: string;
  hint: string;
  keywords: string;
  run: (editor: Editor, range: Range) => void;
}

/** `/`로 여는 블록 메뉴. 파일 선택은 화면이 연결한 `pickFile`이 연다(업로드는 에디터 밖의 일이다). */
export function slashCommands(pickFile: (kind: 'image' | 'file') => void): SlashCommand[] {
  /** 입력한 `/검색어`를 지운 체인을 받아 블록을 바꾼다. */
  const block = (title: string, hint: string, keywords: string, apply: (chain: ChainedCommands) => void): SlashCommand => ({
    title, hint, keywords, run: (editor, range) => apply(editor.chain().focus().deleteRange(range)),
  });
  return [
    block('제목 1', '큰 제목', 'h1 heading 제목', (c) => c.setNode('heading', { level: 1 }).run()),
    block('제목 2', '중간 제목', 'h2 heading 제목', (c) => c.setNode('heading', { level: 2 }).run()),
    block('제목 3', '작은 제목', 'h3 heading 제목', (c) => c.setNode('heading', { level: 3 }).run()),
    block('글머리 목록', '점 목록', 'bullet list 목록', (c) => c.toggleBulletList().run()),
    block('번호 목록', '숫자 목록', 'number ordered list 번호', (c) => c.toggleOrderedList().run()),
    block('할 일 목록', '체크박스', 'todo task checkbox 할일', (c) => c.toggleTaskList().run()),
    block('인용', '인용 블록', 'quote blockquote 인용', (c) => c.toggleBlockquote().run()),
    block('코드 블록', '코드', 'code 코드', (c) => c.toggleCodeBlock().run()),
    block('구분선', '가로선', 'divider hr 구분선', (c) => c.setHorizontalRule().run()),
    block('표', '3×3 표', 'table 표', (c) => c.insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()),
    block('이미지', '이미지 업로드', 'image 이미지 사진', () => pickFile('image')),
    block('파일', '파일 첨부', 'file attach 파일 첨부', () => pickFile('file')),
  ];
}

/** 메뉴 DOM은 직접 만든다 — 팝업 한 개를 위해 렌더러·포털을 끌어오지 않는다. */
class SlashPopup {
  readonly root = document.createElement('div');
  selected = 0;
  props: SuggestionProps<SlashCommand, SlashCommand> | null = null;

  constructor() {
    this.root.className = 'fixed z-50 max-h-72 w-60 overflow-y-auto rounded-lg border border-line bg-surface p-1 shadow-lg';
    document.body.appendChild(this.root);
  }

  update(props: SuggestionProps<SlashCommand, SlashCommand>): void {
    this.props = props;
    this.selected = Math.min(this.selected, Math.max(props.items.length - 1, 0));
    const rect = props.clientRect?.();
    if (rect) {
      this.root.style.left = `${Math.min(rect.left, window.innerWidth - 250)}px`;
      this.root.style.top = `${rect.bottom + 6}px`;
    }
    this.root.replaceChildren(
      ...(props.items.length === 0
        ? [Object.assign(document.createElement('div'), { className: 'px-2 py-1.5 text-[12px] text-muted', textContent: '일치하는 블록이 없다' })]
        : props.items.map((item, index) => this.row(item, index))),
    );
  }

  row(item: SlashCommand, index: number): HTMLElement {
    const el = document.createElement('button');
    el.type = 'button';
    el.className = `flex w-full cursor-pointer flex-col rounded-md px-2 py-1.5 text-left ${index === this.selected ? 'bg-accent-soft' : 'hover:bg-canvas'}`;
    const title = Object.assign(document.createElement('span'), { className: 'text-[13px] text-ink', textContent: item.title });
    const hint = Object.assign(document.createElement('span'), { className: 'text-[11px] text-muted', textContent: item.hint });
    el.append(title, hint);
    // mousedown으로 받는다 — click은 에디터가 포커스를 잃은 뒤라 범위가 어긋난다.
    el.addEventListener('mousedown', (event) => {
      event.preventDefault();
      this.props?.command(item);
    });
    return el;
  }

  key(event: KeyboardEvent): boolean {
    const items = this.props?.items ?? [];
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (items.length === 0) return true;
      this.selected = (this.selected + (event.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length;
      if (this.props) this.update(this.props);
      return true;
    }
    if (event.key === 'Enter' && items[this.selected]) {
      this.props?.command(items[this.selected]);
      return true;
    }
    return false;
  }

  destroy(): void {
    this.root.remove();
  }
}

export const SlashMenu = (commands: SlashCommand[]) =>
  Extension.create({
    name: 'slashMenu',
    addProseMirrorPlugins() {
      return [
        Suggestion<SlashCommand, SlashCommand>({
          editor: this.editor,
          char: '/',
          items: ({ query }) => {
            const needle = query.trim().toLowerCase();
            return commands.filter((c) => `${c.title} ${c.keywords}`.toLowerCase().includes(needle));
          },
          command: ({ editor, range, props }) => props.run(editor, range),
          // `render()`는 플러그인을 만들 때 한 번만 불린다 — 팝업은 열릴 때 만들고 닫힐 때 지운다.
          render: () => {
            let popup: SlashPopup | null = null;
            return {
              onStart: (props) => {
                popup = new SlashPopup();
                popup.update(props);
              },
              onUpdate: (props) => popup?.update(props),
              onKeyDown: ({ event }) => popup?.key(event) ?? false,
              onExit: () => {
                popup?.destroy();
                popup = null;
              },
            };
          },
        }),
      ];
    },
  });
